import { useEffect, useRef } from "react";
import { Users, Heart, Compass, Shield } from "lucide-react";

const features = [
  {
    icon: Users,
    title: "Intimate Groups",
    description:
      "Maximum 12 travelers per journey. Experience India authentically, not as a tourist.",
  },
  {
    icon: Heart,
    title: "Local Spiritual Guides",
    description:
      "Learn from masters who live the traditions—not professional tour guides reading scripts.",
  },
  {
    icon: Compass,
    title: "Ethical Tourism",
    description:
      "We support local ashrams, artisans, and communities. Your journey creates positive impact.",
  },
  {
    icon: Shield,
    title: "Complete Care",
    description:
      "From visa guidance to cultural preparation, we handle every detail so you can focus on your inner journey.",
  },
];

export const WhyUs = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal").forEach((el, i) => {
              setTimeout(() => {
                el.classList.add("visible");
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24 lg:py-32 bg-sand relative overflow-hidden"
    >
      <div className="container mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="reveal text-saffron text-sm tracking-[0.2em] uppercase font-medium">
            Why Journey With Us
          </span>
          <h2 className="reveal font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mt-4 mb-6">
            Travel With <span className="text-maroon">Purpose & Heart</span>
          </h2>
          <p className="reveal text-muted-foreground leading-relaxed">
            We believe spiritual travel should transform not just where you go, 
            but who you become. Our approach is built on authenticity, care, and deep respect 
            for India's sacred traditions.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div
              key={feature.title}
              className="reveal group"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="bg-background rounded-2xl p-8 h-full shadow-soft hover:shadow-elevated transition-all duration-500 hover:-translate-y-1">
                {/* Icon */}
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-saffron/20 to-gold/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <feature.icon className="w-7 h-7 text-saffron" />
                </div>

                {/* Content */}
                <h3 className="font-serif text-xl text-foreground mb-3">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
